import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { XMarkIcon } from "@heroicons/react/24/solid";
import CustomInput from "../../common/CustomInput";
import {
  isArrayError,
  isFieldError,
  isNameError,
  isValidDateOfBirth,
  isValidPhoneNumber,
  isValidPostalCode,
} from "../../../helpers/input-validations";
import Button from "../../common/Button";
import {
  addressOption,
  longTermOption,
  biologicalSexOptions,
  countryOptions,
  genderOptions,
  phoneOptions,
  preferencesOptions,
  primaryLanguageOptions,
  stateOptions,
  raceOptions,
  characteristicsOptions,
  medicationAdministrationOptions,
} from "./options";
import { calculateAge, capitalizeNameWithSpecialChars } from "../../../helpers/functions";

const FirstStep = ({
  onClose,
  currentStep,
  setCurrentStep,
  patientCreateObject,
  setPatientCreateObject,
}) => {
  const [lastName, setLastName] = useState(patientCreateObject.lastName);
  const [middleName, setMiddleName] = useState(patientCreateObject.middleName);
  const [firstName, setFirstName] = useState(patientCreateObject.firstName);
  const [dateOfBirth, setDateOfBirth] = useState(patientCreateObject.dateOfBirth);
  const [biologicalSex, setBiologicalSex] = useState(
    patientCreateObject.biologicalSex
  );
  const [gender, setGender] = useState(patientCreateObject.gender);
  const [race, setRace] = useState(patientCreateObject.race);
  const [characteristics, setCharacteristics] = useState(
    patientCreateObject.characteristics?.split(",")
      .filter((item) => item.trim().length > 0)
      .map((item) => item.trim()) ?? []
  );
  const [medicationAdministration, setMedicationAdministration] = useState(
    patientCreateObject.medicationAdministration ?? "swallowing"
  );
  const [primaryLanguage, setPrimaryLanguage] = useState(
    patientCreateObject.primaryLanguage
  );
  const [noPhone, setNoPhone] = useState(patientCreateObject.noPhone);

  const getPreferenceList = (pref) => {
    return (
      pref
        ?.split(",")
        .filter((item) => item.trim().length > 0)
        .map((item) => item.trim()) ?? []
    );
  };

  const [preferences, setPreferences] = useState(
    getPreferenceList(patientCreateObject.preferences)
  );
  const [primaryPhoneCountry, setPrimaryPhoneCountry] = useState(
    patientCreateObject.primaryPhoneCountry
  );
  const [phoneNumber, setPhoneNumber] = useState(
    patientCreateObject.phoneNumber
  );
  const [secondaryPhoneNumber, setSecondaryPhoneNumber] = useState(
    patientCreateObject.secondaryPhoneNumber
  );
  const [secondaryPhoneCountry, setSecondaryPhoneCountry] = useState(
    patientCreateObject.secondaryPhoneCountry
  );
  const [email, setEmail] = useState(patientCreateObject.email);
  const [noAddress, setNoAddress] = useState(patientCreateObject.noAddress);
  const [longTermCare, setLongTermCare] = useState(
    patientCreateObject.longTermCare ?? false
  );
  const [facility, setFacility] = useState(patientCreateObject.facility);
  const [address, setAddress] = useState(patientCreateObject.address);
  const [unit, setUnit] = useState(patientCreateObject.unit);
  const [poBox, setPoBox] = useState(patientCreateObject.poBox);
  const [city, setCity] = useState(patientCreateObject.city);
  const [state, setState] = useState(patientCreateObject.state);
  const [postalCode, setPostalCode] = useState(patientCreateObject.postalCode);
  const [country, setCountry] = useState(patientCreateObject.country);
  const [lastNameError, setLastNameError] = useState(false);
  const [firstNameError, setFirstNameError] = useState(false);
  const [dateOfBirthError, setDateOfBirthError] = useState(false);
  const [primaryLanguageError, setPrimaryLanguageError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [primaryPhoneError, setPrimaryPhoneError] = useState(false);
  const [cityError, setCityError] = useState(false);
  const [stateError, setStateError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState(false);
  const [characteristicsError, setCharacteristicsError] = useState(false);
  const [administrationError, setAdministrationError] = useState(false);
  const [commentsFirstStep, setCommentsFirstStep] = useState(
    patientCreateObject.commentsFirstStep
  );
  const [clicked, setClicked] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  
  const dispatch = useDispatch();
  const getRequiredBg = (val) => {
    const isEmpty =
      val == null ||
      (typeof val === "string" && val.trim() === "") ||
      (Array.isArray(val) && val.length === 0);

    return isEmpty ? "!bg-blue-100" : "!bg-white";
  };
  
  const normalizeCharacteristicsSelection = (
    previousSelection = [],
    nextSelection = []
  ) => {
    const toValueArray = (collection = []) =>
      collection
        .map((item) => (typeof item === "string" ? item : item?.value))
        .filter(Boolean);

    const prevHadNone = toValueArray(previousSelection).includes("none");
    const nextValues = Array.from(new Set(toValueArray(nextSelection)));

    if (!nextValues.length) {
      return [];
    }

    if (!nextValues.includes("none")) {
      return nextValues;
    }

    if (!prevHadNone) {
      return ["none"];
    }

    const others = nextValues.filter((value) => value !== "none");
    return others.length ? others : ["none"];
  };

  const normalizePreferencesSelection = (
    previousSelection = [],
    nextSelection = []
  ) => {
    const toValueArray = (collection = []) =>
      collection
        .map((item) => (typeof item === "string" ? item : item?.value))
        .filter(Boolean);

    const prevHadNone = toValueArray(previousSelection).includes("none");
    const nextValues = Array.from(new Set(toValueArray(nextSelection)));

    if (!nextValues.length) return [];
    if (!nextValues.includes("none")) return nextValues;
    if (!prevHadNone) return ["none"];

    const others = nextValues.filter((value) => value !== "none");
    return others.length ? others : ["none"];
  };

  const renderPhoneClearIcon = (phoneValue, clearHandler) =>
    phoneValue ? (
      <button
        type="button"
        className="absolute right-4 top-[42px] text-gray-600 hover:text-gray-900"
        onClick={clearHandler}
        aria-label="Clear phone number"
      >
        <XMarkIcon className="w-4 h-4" />
      </button>
    ) : null;

  const fieldsToCheck = [
    {
      field: lastName,
      checkFn: isNameError,
      setErrorFn: setLastNameError,
      alter: false,
      name: "lastName",
    },
    {
      field: firstName,
      checkFn: isNameError,
      setErrorFn: setFirstNameError,
      alter: false,
      name: "firstName",
    },
    {
      field: dateOfBirth,
      checkFn: (value) => Boolean(value) && isValidDateOfBirth(value),
      setErrorFn: setDateOfBirthError,
      alter: true,
      name: "dateOfBirth",
    },
    {
      field: primaryLanguage,
      checkFn: isFieldError,
      setErrorFn: setPrimaryLanguageError,
      alter: false,
      name: "primaryLanguage",
    },
    {
      field: address,
      checkFn: isFieldError,
      setErrorFn: setAddressError,
      alter: false,
      name: "address",
    },
    {
      field: phoneNumber,
      checkFn: isValidPhoneNumber,
      setErrorFn: setPrimaryPhoneError,
      alter: true,
      name: "phoneNumber",
    },
    {
      field: city,
      checkFn: isFieldError,
      setErrorFn: setCityError,
      alter: false,
      name: "city",
    },
    {
      field: state,
      checkFn: isFieldError,
      setErrorFn: setStateError,
      alter: false,
      name: "state",
    },
    {
      field: country,
      checkFn: isFieldError,
      setErrorFn: setCountryError,
      alter: false,
      name: "country",
    },
    {
      field: postalCode,
      checkFn: isValidPostalCode,
      setErrorFn: setPostalCodeError,
      alter: true,
      name: "postalCode",
    },
    {
      field: characteristics,
      checkFn: isArrayError,
      setErrorFn: setCharacteristicsError,
      alter: false,
      name: "characteristics",
    },
    {
      field: medicationAdministration,
      checkFn: isFieldError,
      setErrorFn: setAdministrationError,
      alter: false,
      name: "medicationAdministration",
    },
  ];

  const getFieldsToValidate = () => {
    let fieldsToValidate = fieldsToCheck;
    if (noAddress || longTermCare) {
      fieldsToValidate = fieldsToValidate.filter(
        ({ name }) => name !== "address"
      );
    }

    if (longTermCare) {
      fieldsToValidate = fieldsToValidate.filter(
        ({ name }) => name !== "country"
      );
    }

    if (noPhone) {
      fieldsToValidate = fieldsToValidate.filter(
        ({ name }) => name !== "phoneNumber"
      );
    }

    return fieldsToValidate;
  };

  const hasFieldValidationError = ({ field, checkFn, alter }) =>
    alter ? !checkFn(field) : checkFn(field);

  const isSaveAndContinueDisabled = getFieldsToValidate().some(
    (fieldConfig) => hasFieldValidationError(fieldConfig)
  );

  const manageStep = async () => {
    setClicked(true);
    let allFieldsValid = true;
    const fieldsToValidate = getFieldsToValidate();
    const fieldsToSkip = fieldsToCheck.filter(
      (fieldConfig) => !fieldsToValidate.includes(fieldConfig)
    );

    fieldsToSkip.forEach(({ setErrorFn }) => setErrorFn(false));

    fieldsToValidate.forEach(({ setErrorFn, ...fieldConfig }) => {
      const hasError = hasFieldValidationError(fieldConfig);
      setErrorFn(hasError);

      if (hasError) {
        allFieldsValid = false;
      }
    });

    if (allFieldsValid) {
      // Proceed to next step (duplicate check moved to Insurance step)
      const pref = preferences && preferences.length > 0 ? preferences.join(", ") : null;
      const char = characteristics && characteristics.length > 0 ? characteristics.join(", ") : null;
      setPatientCreateObject({
        ...patientCreateObject,
        lastName,
        middleName,
        firstName,
        dateOfBirth,
        biologicalSex,
        gender,
        race,
        characteristics: char,
        medicationAdministration,
        primaryLanguage,
        commentsFirstStep,
        noPhone,
        preferences: pref,
        primaryPhoneCountry,
        phoneNumber,
        secondaryPhoneNumber,
        secondaryPhoneCountry,
        email,
        noAddress,
        longTermCare,
        facility,
        address,
        unit,
        poBox,
        city,
        state,
        postalCode,
        country,
      });
      setCurrentStep(currentStep + 1);
    }
  };

  useEffect(() => {
    if (clicked) {
      const fieldsToValidate = getFieldsToValidate();
      const fieldsToSkip = fieldsToCheck.filter(
        (fieldConfig) => !fieldsToValidate.includes(fieldConfig)
      );

      fieldsToSkip.forEach(({ setErrorFn }) => setErrorFn(false));

      fieldsToValidate.forEach(({ setErrorFn, ...fieldConfig }) => {
        const hasError = hasFieldValidationError(fieldConfig);
        setErrorFn(hasError);
      });
    }
  }, [clicked, ...fieldsToCheck.map(({ field }) => field)]);

  useEffect(() => {
    if (longTermCare) {
      if (noAddress) {
        setNoAddress(false);
      }
    }
  }, [longTermCare]);

  useEffect(() => {
    if (noAddress) {
      if (longTermCare) {
        setLongTermCare(false);
      }
    }
  }, [noAddress]);


  return (
    <>
      <div
        className="flex flex-col px-8 justify-between flex-1 py-6 h-full overflow-y-auto"
        id="form-body"
      >
        <div className="grid grid-cols-12 gap-3">
          <CustomInput
            name="lastName"
            className={`col-span-4 ${getRequiredBg(lastName)}`}
            label="Last Name"
            placeholder="Enter last name"
            type="text"
            value={lastName}
            required
            onChange={(e) => {
              const capitalized = capitalizeNameWithSpecialChars(e.target.value);
              setLastName(capitalized);
            }}
            error={lastNameError}
            autoFocus={lastNameError}
            focus={lastNameError}
          />
          <CustomInput
            name="middleName"
            className="col-span-4"
            label="Middle Name"
            placeholder="Enter middle name"
            type="text"
            value={middleName}
            onChange={(e) => {
              const capitalized = capitalizeNameWithSpecialChars(e.target.value);
              setMiddleName(capitalized);
            }}
            error={middleName && isNameError(middleName)}
          />
          <CustomInput
            name="firstName"
            className={`col-span-4 ${getRequiredBg(firstName)}`}
            label="First Name"
            placeholder="Enter first name"
            type="text"
            required
            value={firstName}
            onChange={(e) => {
              const capitalized = capitalizeNameWithSpecialChars(e.target.value);
              setFirstName(capitalized);
            }}
            error={firstNameError}
          />
          <div className="col-span-6">
            <CustomInput
              name="dateOfBirth"
              className={getRequiredBg(dateOfBirth)}
              label="Date of Birth"
              type="date"
              value={dateOfBirth}
              required
              disableFuture={true}
              onChange={(value) => setDateOfBirth(value)}
              error={dateOfBirthError}
            />
          </div>
          <CustomInput
            name="age"
            className="col-span-6"
            placeholder={""}
            disabled
            label="Age"
            showOptionalLabel={false}
            required={true}
            type="text"
            value={calculateAge(dateOfBirth) || ""}
          />
          <CustomInput
            options={biologicalSexOptions}
            name="biologicalSex"
            className="col-span-6"
            label="Biological sex"
            value={biologicalSex}
            type="dropdown"
            onChange={(e) => setBiologicalSex(e.value)}
          />
          <CustomInput
            options={raceOptions}
            name="race"
            label="Race"
            className="col-span-6"
            type="dropdown"
            value={race}
            onChange={(e) => setRace(e.value)}
          />
          <CustomInput
            options={genderOptions}
            name="gender"
            label="Gender"
            className="col-span-12"
            type="dropdown"
            value={gender}
            onChange={(e) => setGender(e.value)}
          />
          <CustomInput
            options={primaryLanguageOptions}
            name="primaryLanguage"
            className={`col-span-6 ${getRequiredBg(primaryLanguage)}`}
            label="Primary Language"
            type="dropdown"
            required
            value={primaryLanguage}
            onChange={(e) => setPrimaryLanguage(e.value)}
            error={primaryLanguageError}
          />
          <CustomInput
            options={preferencesOptions}
            name="preferences"
            className="col-span-6"
            value={preferences}
            label="Preferences"
            type="multi-dropdown"
            exclusiveSelectValues={["none"]}
            onChange={(selected) =>
              setPreferences(
                normalizePreferencesSelection(preferences, selected)
              )
            }
          />
          <CustomInput
            options={medicationAdministrationOptions}
            name="medicationAdministration"
            label="Medication Administration"
            className={`col-span-6 ${getRequiredBg(medicationAdministration)}`}
            type="dropdown"
            value={medicationAdministration}
            onChange={(e) => setMedicationAdministration(e.value)}
            error={administrationError}
            required
          />
          <CustomInput
            options={characteristicsOptions.map((x) => {
              return { label: x.header, value: x.value };
            })}
            name="characteristics"
            className={`col-span-6 ${getRequiredBg(characteristics)}`}
            value={characteristics}
            label="Characteristics"
            type="multi-dropdown"
            onChange={(selected) =>
              setCharacteristics(
                normalizeCharacteristicsSelection(characteristics, selected)
              )
            }
            error={characteristicsError}
            required
          />
          <div className="col-span-12 my-3 bg-gray-100 h-[1px] w-full " />
          <p className="col-span-12 font-medium text-gray-900 text-body-md">
            Contact Information
          </p>
          <CustomInput
            vertical
            name="noPhone"
            className="col-span-12"
            value={noPhone}
            options={phoneOptions}
            onChange={(e) => setNoPhone(e.target.checked)}
            type="checkbox"
          />
          {noPhone ? null : (
            <>
              <div className="col-span-6 relative">
                <CustomInput
                  name="phoneNumber"
                  className={getRequiredBg(phoneNumber)}
                  label="Primary Phone Number"
                  value={phoneNumber}
                  selectValue={primaryPhoneCountry}
                  placeholder="(555) 000-0000"
                  type="phone"
                  required
                  handleSelect={(e) => setPrimaryPhoneCountry(e.target.value)}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  error={primaryPhoneError}
                />
                {renderPhoneClearIcon(phoneNumber, () => setPhoneNumber(""))}
              </div>
              <div className="col-span-6 relative">
                <CustomInput
                  name="secondaryPhoneNumber"
                  label="Secondary Phone Number"
                  placeholder="(555) 000-0000"
                  value={secondaryPhoneNumber}
                  selectValue={secondaryPhoneCountry}
                  type="phone"
                  handleSelect={(e) => setSecondaryPhoneCountry(e.target.value)}
                  onChange={(e) => setSecondaryPhoneNumber(e.target.value)}
                />
                {renderPhoneClearIcon(secondaryPhoneNumber, () =>
                  setSecondaryPhoneNumber("")
                )}
              </div>
            </>
          )}
          <CustomInput
            name="email"
            className="col-span-12"
            label="Email Address"
            value={email}
            placeholder="Enter Email Address"
            type="email"
            onChange={(e) => setEmail(e.target.value)}
          />

          <p className="col-span-12 font-medium text-gray-900 text-body-md">
            Address Information
          </p>
          <CustomInput
            horizontal
            name="noAddress"
            className="col-span-6"
            value={noAddress}
            options={addressOption}
            onChange={(e) => setNoAddress(e.target.checked)}
            type="checkbox"
          />
          <CustomInput
            horizontal
            name="longTermCare"
            className="col-span-6"
            value={longTermCare}
            options={longTermOption}
            onChange={(e) => setLongTermCare(e.target.checked)}
            type="checkbox"
          />

          {longTermCare && (
            <>
              <CustomInput
                name="facility"
                className="col-span-12"
                value={facility}
                label="Facility"
                placeholder="Enter Name of Facility"
                type="text"
                onChange={(e) => setFacility(e.target.value)}
              />
            </>
          )}
          {noAddress ? null : (
            <>
              {!longTermCare && (
                <>
                  <CustomInput
                    name="address"
                    className={`col-span-12 ${getRequiredBg(address)}`}
                    value={address}
                    label="Address"
                    placeholder=" Enter Street Number & Street Name"
                    type="text"
                    required
                    onChange={(e) => setAddress(e.target.value)}
                    error={addressError}
                  />
                  <CustomInput
                    name="unit"
                    className="col-span-6"
                    value={unit}
                    label="Unit Number 123"
                    placeholder="Enter Unit Number"
                    type="text"
                    onChange={(e) => setUnit(e.target.value)}
                  />
                  <CustomInput
                    name="poBox"
                    className="col-span-6"
                    label="PO Box"
                    value={poBox}
                    placeholder="PO Box"
                    type="age"
                    onChange={(e) => setPoBox(e.target.value)}
                  />
                </>
              )}
            </>
          )}
          <CustomInput
            name="city"
            className={`col-span-6 ${getRequiredBg(city)}`}
            label="City"
            value={city}
            type="text"
            required
            onChange={(e) => setCity(e.target.value)}
            error={cityError}
          />
          <CustomInput
            options={stateOptions}
            name="state"
            value={state}
            className={`col-span-6 ${getRequiredBg(state)}`}
            label="Province"
            type="dropdown"
            required
            onChange={(e) => setState(e.value)}
            error={stateError}
          />
          {!noAddress && !longTermCare && (
            <>
              <CustomInput
                name="postalCode"
                className="col-span-6"
                label="Postal Code"
                value={postalCode}
                placeholder="Enter Postal Code"
                type="postalCode"
                onChange={(e) => setPostalCode(e.target.value)}
                error={postalCodeError}
              />
            </>
          )}
          {!longTermCare && (
            <CustomInput
              options={countryOptions}
              name="countryOptions"
              className={`col-span-6 ${getRequiredBg(country)}`}
              value={country}
              label="Country"
              type="dropdown"
              required
              onChange={(e) => setCountry(e.value)}
              error={countryError}
            />
          )}

          <CustomInput
            name="commentsFirstStep"
            className="col-span-12"
            label="Comments"
            value={commentsFirstStep}
            placeholder="Enter Note"
            type="text"
            onChange={(e) => setCommentsFirstStep(e.target.value)}
          />
        </div>
      </div>
      <div className="flex px-8 items-center justify-center py-6">
        <div className="flex items-center justify-center space-x-5">
          <Button
            className="min-w-[200px]"
            text={currentStep === 1 ? `Cancel` : "Go Back"}
            type="secondary"
            onClick={() =>
              currentStep === 1
                ? onClose(false)
                : setCurrentStep(currentStep - 1)
            }
          />
          <Button
            className="min-w-[200px]"
            text="Save & Continue"
            type="primary"
            disabled={isSaveAndContinueDisabled}
            onClick={() => manageStep()}
          />
        </div>
      </div>
      
    </>
  );
};

export default FirstStep;
